import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import { authApi, inviteApi } from '../lib/api';
import { InstallAppButton } from './InstallAppButton';
import { setTenantId as saveTenantId, sanitizeTenantId } from '../lib/tenant-utils';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useAuth } from '../lib/auth-context';
import { useI18n } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Lock, Delete, ArrowLeft, Languages } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChangePinDialog } from './ChangePinDialog';
import { projectId } from '../../../utils/supabase/info';
import { LANGUAGES, LANGUAGE_CATEGORIES } from '../lib/languages';
import { LanguagePicker } from './LanguagePicker';
import { softwareName, detectSector, pageTitle } from '../lib/software-name';
import { tx } from '../lib/tx';
import { SESSION_NOTICE_KEY, PLAN_FREE_ROLE_NOTICE_KEY } from '../lib/session-token';
import { planUiText } from '../lib/plan-texts';
import { APP_VERSION, BUILD_NUMBER, BUILD_DATE } from '../version';

// API Base URL for error messages
const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-df8bdc4f`;

const SERVICE_TYPE_LABELS: Record<string, { icon: string; name: string }> = {
  dryCleaning:   { icon: '🧺', name: 'Kuru Temizleme' },
  carpetWashing: { icon: '🪣', name: 'Halı Yıkama' },
  sofaWashing:   { icon: '🛋️', name: 'Koltuk Yıkama' },
  carWash:       { icon: '🚗', name: 'Oto Yıkama' },
  shoeShine:     { icon: '👟', name: 'Lostra' },
  tailoring:     { icon: '✂️', name: 'Terzi Tadilat' },
  barber:        { icon: '💈', name: 'Berber / Kuaför' },
};

// ✅ LoginPage Component
// Google düğmesi ve altında "veya" ayırıcı; düğme çizilmezse (istemci kimliği yok) ayırıcı da gizlenir
function GoogleLoginBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 [&:not(:has(iframe))]:hidden">
      {children}
      <div className="flex items-center gap-3 mt-4 text-xs text-gray-400">
        <span className="flex-1 h-px bg-gray-200" />veya<span className="flex-1 h-px bg-gray-200" />
      </div>
    </div>
  );
}

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [showPinPad, setShowPinPad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [showChangePinDialog, setShowChangePinDialog] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<any>(null);
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [tenantServiceType, setTenantServiceType] = useState('');
  const [tenantCompanyName, setTenantCompanyName] = useState('');
  const tenantInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { login, user } = useAuth();
  
  // Show signup success message if coming from signup
  const [signupMessage, setSignupMessage] = useState('');
  const [newCredentials, setNewCredentials] = useState<any>(null);
  useEffect(() => {
    if (location.state?.message) {
      setSignupMessage(location.state.message);
    }
    if (location.state?.credentials) {
      setNewCredentials(location.state.credentials);
    }
    // SignupPage'den gelen davet kodu — localStorage'a yaz (zaten orada olabilir)
    if (location.state?.inviteCode && !localStorage.getItem('pendingInviteCode')) {
      localStorage.setItem('pendingInviteCode', location.state.inviteCode);
    }
  }, [location.state]);
  
  // Yeni tenant için varsayılan şifre bilgisi
  const [showDefaultPasswordInfo, setShowDefaultPasswordInfo] = useState(false);
  useEffect(() => {
    const newTenantFlag = localStorage.getItem('newTenantCreated');
    if (newTenantFlag === 'true') {
      setShowDefaultPasswordInfo(true);
    }
  }, []);
  
  const { t, language, setLanguage, isLoading: i18nLoading } = useI18n();
  // Ücretsiz pakette patron dışı giriş 402 PLAN_LIMIT (users) alır: satır içi uyarı (pencere açılmaz, kullanıcı içeride değil)
  const [planLoginError, setPlanLoginError] = useState(false);

  // Oturum belgesi yok / süresi dolmuş olduğu için çıkış yapıldıysa bir kez açıklama göster
  useEffect(() => {
    try {
      if (localStorage.getItem(SESSION_NOTICE_KEY)) {
        localStorage.removeItem(SESSION_NOTICE_KEY);
        toast.info(tx(language, 'Güvenlik güncellemesi: lütfen tekrar giriş yapın.', 'Security update: please sign in again.'), { duration: 8000 });
      }
      // Ücretsiz pakette patron dışı açık oturum kapatıldı (R7): girişteki "yalnız patron" uyarısı gösterilir
      if (localStorage.getItem(PLAN_FREE_ROLE_NOTICE_KEY)) {
        localStorage.removeItem(PLAN_FREE_ROLE_NOTICE_KEY);
        setPlanLoginError(true);
      }
    } catch { /* */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sekme başlığı: sektör biliniyorsa "Cleandy Berber Programı", bilinmiyorsa genel ad (seçilen dilde)
  useEffect(() => {
    document.title = pageTitle(detectSector(searchParams.get('sector'), localStorage), language);
  }, [searchParams, language]);

  useEffect(() => {
    if (user) {
      if (user.role === 'şoför') {
        navigate('/driver');
      } else {
        navigate('/');
      }
    }
  }, [user, navigate]);

  // Initialize tenantId from URL or localStorage
  useEffect(() => {
    const urlTenantId = searchParams.get('tenantId');
    if (urlTenantId) {
      const sanitized = saveTenantId(urlTenantId);
      setTenantId(sanitized);
      window.history.replaceState({}, '', '/login');
    } else {
      // İlk girişte boş gelir (demo yazmaz); daha önce giriş yapıldıysa firma hatırlanır
      const savedTenantId = localStorage.getItem('tenantId');
      if (savedTenantId && savedTenantId !== 'demo') setTenantId(savedTenantId);
    }
    // Son giriş yapan kullanıcı adını hatırla
    const lastUser = localStorage.getItem('lastUsername');
    if (lastUser) setUsername(lastUser);
  }, [searchParams]);

  // Google ile giriş: birden fazla firma eşleşirse seçim listesi
  const [googleTenants, setGoogleTenants] = useState<{ id: string; name: string }[] | null>(null);
  const googleCredentialRef = useRef<string>('');

  if (i18nLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const handlePinInput = (digit: string) => {
    if (pin.length < 6) {
      setPin(pin + digit);
    }
  };

  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  const handleLogin = async () => {
    if (!tenantId.trim()) {
      toast.error(t('login.tenantId'));
      return;
    }
    if (!username.trim()) {
      toast.error(t('login.usernameRequired'));
      return;
    }

    if (pin.length < 4) {
      toast.error(t('login.pinMinLength'));
      return;
    }

    setLoading(true);
    setPlanLoginError(false);
    try {
      saveTenantId(tenantId);
      const response = await authApi.login(pin, username);
      
      if (response.success && response.user) {
        localStorage.setItem('hasLoggedInBefore', 'true');
        localStorage.setItem('lastUsername', username.trim()); // sonraki girişte hatırla
        localStorage.removeItem('newTenantCreated');
        setShowDefaultPasswordInfo(false);

        // İmzalı oturum token'ı — fetchApi bunu X-Session-Token ile gönderir
        if (response.sessionToken) {
          localStorage.setItem('sessionToken', response.sessionToken);
        }
        
        // Şube bilgisini localStorage'a kaydet (fetchApi X-Branch-ID header için)
        if (response.user.branch_id) {
          localStorage.setItem('branchId', response.user.branch_id);
        } else {
          localStorage.removeItem('branchId');
        }

        if (response.requiresPasswordChange) {
          setLoggedInUser(response.user);
          setShowChangePinDialog(true);
        } else {
          login({
            ...response.user,
            branchId: response.user.branch_id || null,
            branchName: response.user.branch_name || null,
          });
          toast.success(t('login.welcome').replace('{name}', response.user.name));
          if (response.user.role === 'şoför') {
            navigate('/driver');
          } else {
            // pendingInviteCode varsa MainLayout halleder; buradan sadece '/' e git
            navigate('/');
          }
        }
      }
    } catch (error: any) {
      if (error?.code === 'PLAN_LIMIT' || error?.message === 'PLAN_LIMIT') {
        setPlanLoginError(true);
        setPin('');
        return;
      }
      const errorMsg = error.message || t('login.loginFailed');
      toast.error(errorMsg);
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // ── Google ile giriş (yalnızca patron) ──

  const handleGoogleLogin = async (credential: string, chosenTenant?: string) => {
    googleCredentialRef.current = credential;
    setLoading(true);
    try {
      const response: any = await authApi.googleLogin(credential, chosenTenant);
      if (response?.needsTenant) {
        setGoogleTenants(response.tenants || []);
        return;
      }
      if (response?.success && response.user && response.tenantId) {
        setGoogleTenants(null);
        saveTenantId(response.tenantId);
        setTenantId(response.tenantId);
        localStorage.setItem('hasLoggedInBefore', 'true');
        if (response.user.username) localStorage.setItem('lastUsername', response.user.username);
        if (response.sessionToken) localStorage.setItem('sessionToken', response.sessionToken);
        if (response.user.branch_id) localStorage.setItem('branchId', response.user.branch_id);
        else localStorage.removeItem('branchId');
        login({
          ...response.user,
          branchId: response.user.branch_id || null,
          branchName: response.user.branch_name || null,
        });
        toast.success(t('login.welcome').replace('{name}', response.user.name));
        navigate('/');
      }
    } catch (error: any) {
      toast.error(error.message || t('login.loginFailed'), { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Google Translate can corrupt React state by mutating the DOM — read from the real input as fallback
    // Kiril/Türkçe/aksanlı firma kodunu Latin'e çevir (signup ile birebir aynı) — химчистка -> himchistka
    const effectiveTenantId = sanitizeTenantId(tenantId || tenantInputRef.current?.value || '');
    if (!effectiveTenantId || effectiveTenantId.length < 3) {
      toast.error(t('login.invalidTenantId'));
      return;
    }
    if (effectiveTenantId !== tenantId) {
      setTenantId(effectiveTenantId);
    }

    setInitializing(true);
    try {
      saveTenantId(effectiveTenantId);
      const response = await authApi.checkTenant(effectiveTenantId);
      
      if (response.exists) {
        setTenantServiceType(response.serviceType || 'dryCleaning');
        setTenantCompanyName(response.companyName || '');

        if (signupMessage) {
          toast.success(signupMessage, { duration: 8000 });
          setSignupMessage('');
        }

        if (newCredentials) {
          const credsMessage = `🔑 ${t('login.credentialsTitle')}:\n\n${t('settings.company')}: ${newCredentials.companyCode}\n${t('login.username')}: patron\n\n📧 PIN kodunuz e-posta adresinize gönderildi.\n\n${t('login.saveCredentialsNote')}`;
          toast.info(credsMessage, { duration: 15000 });
          setNewCredentials(null);
        }

        setShowPinPad(true);
      } else {
        toast.error(t('login.tenantNotFound').replace('{tenantId}', effectiveTenantId));
        localStorage.removeItem('tenantId');
        setTenantId('');
      }
    } catch (error: any) {
      // Ağ hatasında firma kodu silinmez: açık başka sekmedeki oturumu bozuyordu
      toast.error(error.message);
    } finally {
      setInitializing(false);
    }
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('login.systemInitializing')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-2 sm:p-4">
      {/* Language Selector */}
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 z-10">
        <LanguagePicker />
      </div>

      <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md">
        <>
            <div className="text-center mb-4 sm:mb-6">
              <div className="mb-3 sm:mb-4">
                <img src="/logo.jpg" alt="Cleandy" className="h-10 sm:h-14 mx-auto object-contain" />
              </div>
              {/* Logo zaten "Cleandy" yazıyor; ayrı başlık yalnızca ekran okuyucular için */}
              <h1 className="sr-only">{t('login.title')}</h1>
              <p className="text-xs sm:text-sm text-gray-600">{t('login.subtitle')}</p>
              <div className="mt-3 flex justify-center">
                <InstallAppButton />
              </div>
            </div>

            {/* Google ile giriş (patron) — formun üstünde; istemci kimliği yoksa görünmez.
                Yazılı firma kodu da gönderilir: patronda ön seçim, destek (admin) girişinde hedef firma. */}
            <GoogleLoginBlock>
              <GoogleSignInButton
                onCredential={(cred) => handleGoogleLogin(cred, sanitizeTenantId(tenantId || '') || undefined)}
                language={language}
              />
            </GoogleLoginBlock>

            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-3">
              {/* Firma Kodu */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('login.tenantId')}</label>
                <Input
                  ref={tenantInputRef}
                  type="text"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value.toLowerCase().trim())}
                  placeholder={t('login.tenantPlaceholder')}
                  className="h-10 sm:h-11 text-base text-center notranslate"
                  autoFocus
                  translate="no"
                  spellCheck={false}
                  autoComplete="off"
                  disabled={loading}
                />
              </div>

              {/* Kullanıcı Adı */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('login.username')}</label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setPlanLoginError(false); }}
                  placeholder={t('login.usernamePlaceholder')}
                  className="h-10 sm:h-11 text-base"
                  disabled={loading}
                />
              </div>

              {/* Şifre (PIN) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Şifre (PIN)</label>
                <div className="flex justify-center gap-1.5 mb-3">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-md border flex items-center justify-center text-lg font-bold transition-all ${
                        i < pin.length ? 'bg-blue-600 border-blue-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-400'
                      }`}
                    >
                      {i < pin.length ? '●' : ''}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                    <Button
                      key={digit}
                      type="button"
                      onClick={() => handlePinInput(digit.toString())}
                      className="h-10 sm:h-12 text-xl font-semibold bg-white border border-gray-300 text-gray-800 hover:bg-blue-50 shadow-sm"
                      disabled={loading}
                    >
                      {digit}
                    </Button>
                  ))}
                  <Button type="button" onClick={handleClear} className="h-10 sm:h-12 text-sm font-semibold bg-red-50 border border-red-200 text-red-600" disabled={loading}>
                    {t('login.clear')}
                  </Button>
                  <Button type="button" onClick={() => handlePinInput('0')} className="h-10 sm:h-12 text-xl font-semibold bg-white border border-gray-300 text-gray-800" disabled={loading}>
                    0
                  </Button>
                  <Button type="button" onClick={handleBackspace} className="h-10 sm:h-12 text-xs font-semibold bg-gray-50 border border-gray-300 text-gray-600 flex items-center justify-center" disabled={loading}>
                    <Delete className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {planLoginError && (
                <div role="alert" className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
                  {planUiText(language).loginFreeOnlyPatron}
                </div>
              )}

              <Button
                type="submit"
                className="w-full mt-1 h-11 sm:h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                disabled={loading || !tenantId.trim() || pin.length < 4 || !username.trim()}
              >
                {loading ? t('login.loggingIn') : t('login.login')}
              </Button>

              <button
                type="button"
                onClick={() => { setShowForgotPin(true); setForgotEmail(''); }}
                className="w-full mt-1 text-xs text-gray-400 hover:text-blue-500 transition-colors text-center"
              >
                PIN kodunu mu unuttunuz?
              </button>

              <div className="text-center pt-3 border-t">
                <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">{t('login.notMember')}</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/signup')}
                  className="w-full h-10 sm:h-11 text-sm sm:text-base text-blue-600 border-blue-300 font-semibold"
                >
                  ✨ {t('login.startTrial')}
                </Button>
              </div>
            </form>
          </>

        {/* Google ile girişte birden fazla firma: seçim */}
        {googleTenants && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
              <h2 className="text-lg font-bold text-gray-800 mb-1">Firma seçin</h2>
              <p className="text-sm text-gray-500 mb-4">Bu Google hesabı birden fazla firmaya bağlı.</p>
              <div className="space-y-2">
                {googleTenants.map(tn => (
                  <button key={tn.id} type="button" disabled={loading}
                    onClick={() => handleGoogleLogin(googleCredentialRef.current, tn.id)}
                    className="w-full text-left px-4 py-3 rounded-lg border hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50">
                    <div className="font-medium text-gray-800">{tn.name}</div>
                    <div className="text-xs text-gray-500">{tn.id}</div>
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setGoogleTenants(null)} className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Forgot PIN Dialog */}
        {showForgotPin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
              <h2 className="text-lg font-bold text-gray-800 mb-1">PIN Sıfırlama</h2>
              <p className="text-sm text-gray-500 mb-4">
                Kayıtlı e-posta adresinizi girin. Geçici PIN gönderilecektir.
              </p>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="ornek@email.com"
                className="mb-3 h-11"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowForgotPin(false)}
                  disabled={forgotLoading}
                >
                  İptal
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={forgotLoading || !forgotEmail.trim()}
                  onClick={async () => {
                    if (!forgotEmail.trim()) return;
                    setForgotLoading(true);
                    try {
                      const res = await fetch(`${API_BASE}/auth/forgot-pin`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ tenantId, email: forgotEmail.trim() }),
                      });
                      const data = await res.json();
                      if (res.ok) {
                        toast.success('Geçici PIN e-posta adresinize gönderildi (kayıtlıysa).');
                        setShowForgotPin(false);
                      } else {
                        toast.error(data.error || 'İşlem başarısız');
                      }
                    } catch {
                      toast.error('Bağlantı hatası');
                    } finally {
                      setForgotLoading(false);
                    }
                  }}
                >
                  {forgotLoading ? 'Gönderiliyor…' : 'Gönder'}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <div className="text-center mt-3 pt-2 border-t border-gray-100">
          {/* SEO backlink — rel="noopener" only, intentionally no nofollow */}
          <a
            href="https://www.cleandysoft.com"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-blue-500 transition-colors"
          >
            <img
              src="https://www.cleandysoft.com/cleandy_web_optimized.png"
              alt={softwareName(detectSector(searchParams.get('sector'), localStorage), language)}
              className="h-3.5 object-contain opacity-60"
            />
            <span>{softwareName(detectSector(searchParams.get('sector'), localStorage), language)}</span>
          </a>
          {(() => {
            const euText = t('footer.euServers');
            const gdprText = t('footer.gdpr');
            const hasEu = euText && euText !== 'footer.euServers';
            const hasGdpr = gdprText && gdprText !== 'footer.gdpr';
            return hasEu || hasGdpr ? (
              <p className="text-[9px] text-gray-400 mt-1">
                🇪🇺 {hasEu ? euText : ''} {hasGdpr ? `• ${gdprText}` : ''} • v{APP_VERSION}
              </p>
            ) : (
              <p className="text-[9px] text-gray-400 mt-1">v{APP_VERSION}</p>
            );
          })()}
        </div>
      </div>

      {showChangePinDialog && loggedInUser && (
        <ChangePinDialog
          open={showChangePinDialog}
          userId={loggedInUser.id}
          isFirstLogin={true}
          onSuccess={() => {
            login(loggedInUser);
            toast.success(t('login.welcome').replace('{name}', loggedInUser.name));
            setShowChangePinDialog(false);
            if (loggedInUser.role === 'şoför') navigate('/driver');
            else navigate('/settings');
          }}
        />
      )}
    </div>
  );
}
